import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import type { AutocompleteListItem } from '../types';

export interface AutocompleteListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface AutocompleteListProps {
  items: AutocompleteListItem[];
  command: (item: AutocompleteListItem) => void;
}

export const AutocompleteList = forwardRef<AutocompleteListRef, AutocompleteListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item && !item.isError) {
        command(item);
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) =>
            prev <= 0 ? items.length - 1 : prev - 1
          );
          return true;
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) =>
            prev >= items.length - 1 ? 0 : prev + 1
          );
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          selectItem(selectedIndex);
          return true;
        }

        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="autocomplete-list">
          <div className="autocomplete-empty">No suggestions</div>
        </div>
      );
    }

    return (
      <div className="autocomplete-list">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`autocomplete-item ${
              index === selectedIndex ? 'selected' : ''
            } ${item.isError ? 'error' : ''}`}
            onClick={() => !item.isError && selectItem(index)}
            disabled={item.isError}
          >
            <div className="autocomplete-item-content">
              <span className="autocomplete-item-label">{item.label}</span>
              {item.expandsTo && (
                <span className="autocomplete-item-expansion">
                  → <code>{item.expandsTo}</code>
                </span>
              )}
            </div>
            {item.description && (
              <span
                className={
                  item.isError
                    ? 'autocomplete-error-message'
                    : 'autocomplete-item-description'
                }
              >
                {item.description}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }
);

AutocompleteList.displayName = 'AutocompleteList';

export default AutocompleteList;
